import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { CsvButton, Stat } from "./salesUi";

const METHODS = [{ v: "GCASH", l: "GCash" }, { v: "MAYA", l: "Maya" }, { v: "BANK", l: "Bank" }];

function RequestForm({ data, onDone }) {
  const { data: banks } = useFetch("/commission/banks");
  const p = data.payout || {};
  const [f, setF] = useState({ amount: "", method: p.method || "GCASH", bank_name: p.method === "BANK" ? p.bank_name : "", account_name: p.account_name || "", account_number: p.account_number || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const amt = Number(f.amount) || 0;
  const fee = Math.round(amt * data.settings.fee_pct) / 100;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/commission/requests", { ...f, amount: amt, bank_name: f.method === "BANK" ? f.bank_name : null });
      toast.success("Disbursement request sent to admin"); onDone();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  if (data.requested_this_week) return <div className="surface p-5 text-sm text-slate-600" data-testid="weekly-limit-note">You already requested a disbursement this week ({data.week}). You can request again next week.</div>;
  return (
    <form onSubmit={submit} className="surface space-y-3 p-5" data-testid="disbursement-form">
      <p className="font-bold">Request weekly disbursement</p>
      <Field label={`Amount (available ${money(data.available)})`}><input type="number" step="0.01" min={data.settings.min_request} max={data.available} className="field" value={f.amount} onChange={set("amount")} required data-testid="disb-amount-input" /></Field>
      <div className="grid grid-cols-3 gap-2" data-testid="disb-method-group">
        {METHODS.map((m) => <button type="button" key={m.v} onClick={() => setF({ ...f, method: m.v })} className={`rounded-xl border px-3 py-2 text-sm font-bold ${f.method === m.v ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200"}`} data-testid={`disb-method-${m.v.toLowerCase()}`}>{m.l}</button>)}
      </div>
      {f.method === "BANK" && (
        <Field label="Bank"><select className="field" value={f.bank_name} onChange={set("bank_name")} required data-testid="disb-bank-select">
          <option value="">Select bank</option>{(banks || []).map((b) => <option key={b}>{b}</option>)}
        </select></Field>
      )}
      <Field label="Account name"><input className="field" value={f.account_name} onChange={set("account_name")} required minLength={2} data-testid="disb-account-name-input" /></Field>
      <Field label={f.method === "BANK" ? "Account number" : "Mobile number (09XXXXXXXXX)"}><input className="field" inputMode="numeric" value={f.account_number} onChange={set("account_number")} required data-testid="disb-account-number-input" /></Field>
      <div className="rounded-xl bg-slate-50 p-3 text-sm" data-testid="disb-breakdown">
        <div className="flex justify-between"><span>Requested</span><b>{money(amt)}</b></div>
        <div className="flex justify-between text-rose-600"><span>Processing fee ({data.settings.fee_pct}%)</span><b>− {money(fee)}</b></div>
        <div className="flex justify-between border-t pt-1"><span>You receive</span><b data-testid="disb-net">{money(amt - fee)}</b></div>
      </div>
      <ErrorBox error={error} />
      <button className="btn-primary w-full py-3" disabled={busy || data.available < data.settings.min_request} data-testid="disb-submit-button">{busy && <Loader2 size={16} className="animate-spin" />} Submit to admin</button>
      {data.available < data.settings.min_request && <p className="text-center text-xs text-slate-500">Minimum request is {money(data.settings.min_request)}.</p>}
    </form>
  );
}

const ReqRow = ({ r, admin, children }) => (
  <tr data-testid={`disb-row-${r.id}`}>
    <td className="text-xs">{fmtDateTime(r.created_at)}<br /><span className="text-slate-500">{r.week}</span></td>
    {admin && <td className="font-semibold">{r.user_name}</td>}
    <td>{money(r.gross)}</td><td className="text-rose-600">{money(r.fee)}</td><td className="font-bold">{money(r.net)}</td>
    <td className="text-xs">{r.bank_name}<br />{r.account_name} · <span className="font-mono">{r.account_number}</span></td>
    <td><StatusBadge status={r.status} />{r.reference && <div className="font-mono text-[11px]">{r.reference}</div>}{r.note && <div className="text-[11px] text-slate-500">{r.note}</div>}</td>
    {admin && <td>{children}</td>}
  </tr>
);

const ReqTable = ({ rows, admin, actions }) => (
  <div className="surface overflow-x-auto"><table className="data-table" data-testid="disb-table">
    <thead><tr><th>Requested</th>{admin && <th>Sales</th>}<th>Amount</th><th>Fee</th><th>Net</th><th>Payout to</th><th>Status</th>{admin && <th />}</tr></thead>
    <tbody>{rows.map((r) => <ReqRow key={r.id} r={r} admin={admin}>{actions?.(r)}</ReqRow>)}</tbody>
  </table></div>
);

export default function Commission() {
  const { data, reload } = useFetch("/commission/me");
  if (!data) return <Spinner />;
  const cur = data.months[0];
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <PageHead eyebrow="Sales Force" title="Commission" sub={`${data.settings.rate_pct}% standard commission on verified sales, plus volume bonuses.`} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="This month" value={money(cur.total)} sub={`${money(cur.volume)} volume`} testId="stat-month-commission" />
        <Stat label="Total earned" value={money(data.earned)} testId="stat-earned" />
        <Stat label="Paid out" value={money(data.paid_out)} testId="stat-paid-out" />
        <Stat label="Available" value={money(data.available)} testId="stat-available" />
      </div>
      {cur.next_tier && <div className="surface p-4 text-sm" data-testid="next-bonus-note">Sell <b>{money(cur.next_tier.to_go)}</b> more this month to reach {money(cur.next_tier.min_volume)} and earn a <b>{money(cur.next_tier.bonus)}</b> bonus.</div>}
      <section className="space-y-2" data-testid="commission-months">
        <p className="label">Monthly summary</p>
        <div className="surface overflow-x-auto"><table className="data-table">
          <thead><tr><th>Month</th><th>Volume</th><th>Commission</th><th>Bonus</th><th>Total</th></tr></thead>
          <tbody>{data.months.map((m) => <tr key={m.period} data-testid={`month-${m.period}`}><td>{m.period}</td><td>{money(m.volume)}</td><td>{money(m.commission)}</td><td>{money(m.bonus)}</td><td className="font-bold">{money(m.total)}</td></tr>)}</tbody>
        </table></div>
      </section>
      <RequestForm data={data} onDone={reload} />
      <section className="space-y-2"><p className="label">My disbursement requests</p>
        {!data.requests.length ? <Empty title="No requests yet" /> : <ReqTable rows={data.requests} />}
      </section>
    </div>
  );
}

function SettingsCard() {
  const { user } = useAuth();
  const { data } = useFetch("/commission/settings");
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) { setS(data); } }, [data]);
  if (!s) return <Spinner />;
  const editable = user.role === "super_admin";
  const num = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const tier = (i, k) => (e) => setS({ ...s, tiers: s.tiers.map((t, j) => (j === i ? { ...t, [k]: e.target.value } : t)) });
  const save = async () => {
    setBusy(true);
    try {
      const body = { rate_pct: +s.rate_pct, fee_pct: +s.fee_pct, min_request: +s.min_request, tiers: s.tiers.map((t) => ({ min_volume: +t.min_volume, bonus: +t.bonus })) };
      setS((await api.put("/commission/settings", body)).data); toast.success("Commission settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="surface space-y-3 p-5" data-testid="commission-settings">
      <p className="font-bold">Commission settings {!editable && <span className="text-xs font-normal text-slate-500">· only the Super Admin can edit</span>}</p>
      <fieldset disabled={!editable} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Commission %"><input type="number" step="0.01" className="field" value={s.rate_pct} onChange={num("rate_pct")} data-testid="set-rate-input" /></Field>
        <Field label="Processing fee %"><input type="number" step="0.01" className="field" value={s.fee_pct} onChange={num("fee_pct")} data-testid="set-fee-input" /></Field>
        <Field label="Minimum request ₱"><input type="number" className="field" value={s.min_request} onChange={num("min_request")} data-testid="set-min-input" /></Field>
      </fieldset>
      <p className="label">Volume bonus tiers (monthly sales volume → bonus)</p>
      {!s.tiers.length && <p className="text-xs text-slate-500">No bonus tiers yet.</p>}
      {s.tiers.map((t, i) => (
        <fieldset key={i} disabled={!editable} className="flex items-end gap-2" data-testid={`tier-${i}`}>
          <Field label="When volume reaches ₱"><input type="number" className="field" value={t.min_volume} onChange={tier(i, "min_volume")} data-testid={`tier-${i}-volume`} /></Field>
          <Field label="Bonus ₱"><input type="number" className="field" value={t.bonus} onChange={tier(i, "bonus")} data-testid={`tier-${i}-bonus`} /></Field>
          {editable && <button type="button" className="btn-ghost btn-sm mb-1" onClick={() => setS({ ...s, tiers: s.tiers.filter((_, j) => j !== i) })} data-testid={`tier-${i}-remove`}><Trash2 size={14} /></button>}
        </fieldset>
      ))}
      {editable && <div className="flex gap-2">
        <button type="button" className="btn-ghost btn-sm" onClick={() => setS({ ...s, tiers: [...s.tiers, { min_volume: "", bonus: "" }] })} data-testid="add-tier-button"><Plus size={14} /> Add tier</button>
        <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={save} data-testid="save-commission-settings">{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
      </div>}
    </section>
  );
}

export function CommissionAdmin() {
  const [status, setStatus] = useState("PENDING");
  const { data, reload } = useFetch(`/commission/admin/requests${status ? `?status=${status}` : ""}`, [status]);
  const decide = async (r, action) => {
    const val = window.prompt(action === "PAID" ? `Transfer reference for ${money(r.net)} to ${r.bank_name} ${r.account_number}` : "Reason for rejecting");
    if (!val) return;
    try {
      await api.post(`/commission/admin/requests/${r.id}/decision`, action === "PAID" ? { action, reference: val } : { action, note: val });
      toast.success(action === "PAID" ? "Marked as paid" : "Request rejected"); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const rows = (data || []).map((r) => ({ requested: r.created_at, week: r.week, sales: r.user_name, gross: r.gross, fee: r.fee, net: r.net, method: r.method, bank: r.bank_name, account_name: r.account_name, account_number: r.account_number, status: r.status, reference: r.reference || "" }));
  return (
    <div className="space-y-5">
      <PageHead eyebrow="Sales Force" title="Commission & Disbursements" sub="Weekly payout requests from the sales team." actions={<CsvButton name="giacare-disbursements.csv" rows={rows} />} />
      <SettingsCard />
      <div className="flex gap-2" data-testid="disb-status-filter">
        {["PENDING", "PAID", "REJECTED", ""].map((s) => <button key={s || "ALL"} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1 text-xs font-bold ${status === s ? "bg-blue-600 text-white" : "bg-slate-100"}`} data-testid={`disb-filter-${(s || "all").toLowerCase()}`}>{s || "ALL"}</button>)}
      </div>
      {!data ? <Spinner /> : !data.length ? <Empty title="No requests" /> : <ReqTable rows={data} admin actions={(r) => r.status === "PENDING" && (
        <div className="flex gap-1">
          <button className="btn-primary btn-sm" onClick={() => decide(r, "PAID")} data-testid={`disb-pay-${r.id}`}>Mark paid</button>
          <button className="btn-ghost btn-sm" onClick={() => decide(r, "REJECTED")} data-testid={`disb-reject-${r.id}`}>Reject</button>
        </div>
      )} />}
    </div>
  );
}
